import ScrollModal from "@/components/common/scroll-modal";
import {
  Heading,
  Button,
  TextField,
  Avatar,
  AvatarFallback,
  Icon,
} from "@elaniintc/goes-design-system";
import { newSubFlowSchema } from "@/schemas/processes";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { AxiosError } from "axios";
import SelectField from "@/components/common/select";
import { useGetFlowsByProcessIdAsInfinite } from "@/queries/flows/flows.api";

type ApiErrors = {
  name?: string[];
};

interface ApiErrorResponse {
  errors?: ApiErrors;
}

interface NewFlowModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; parent_flow_id: string }) => void;
  isLoading?: boolean;
  error?: AxiosError<ApiErrorResponse>;
  processId: string;
  currentVersion?: number;
}

const initialState = {
  name: "",
  parent_flow_id: "",
};

export default function NewFlowModal({
  open,
  onClose,
  onSubmit,
  isLoading = false,
  error,
  processId,
  currentVersion,
}: NewFlowModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<{
    name: string;
    parent_flow_id: string;
  }>({
    resolver: zodResolver(newSubFlowSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: initialState,
  });

  const parent_flow_id = watch("parent_flow_id");

  const handleCloseModal = () => {
    reset(initialState);
    onClose();
  };

  useEffect(() => {
    const serverNameErr = error?.response?.data?.errors?.name;
    if (serverNameErr?.length) {
      setError("name", { type: "server", message: serverNameErr[0] });
    }
  }, [error, setError]);

  const {
    data: flows,
    isFetching: isLoadingFlows,
    hasNextPage,
    fetchNextPage,
  } = useGetFlowsByProcessIdAsInfinite(processId, {
    perPage: 20,
    search: "",
    order_by: "created_at",
  });

  const selectOptions = useMemo(() => {
    if (!flows) return [];
    return flows.pages
      .flatMap((page) => page.data)
      .filter((flow) => flow.version === currentVersion) // Filtro para solo mostrar version actual
      .map((flow) => {
        const isParentFlow = !flow.parent_flow_id;
        const avatar = isParentFlow ? (
          <Avatar shape="square" size="2xs">
            <AvatarFallback color="action" shape="square">
              <Icon name="user-circle" type="fas" />
            </AvatarFallback>
          </Avatar>
        ) : (
          <Avatar shape="square" size="2xs">
            <AvatarFallback color="info" shape="square">
              <Icon name="users-rectangle" type="fas" />
            </AvatarFallback>
          </Avatar>
        );
        return {
          label: `${flow.name} V${flow.version}`,
          value: flow.id!,
          avatar,
        };
      });
  }, [flows, currentVersion]);

  return (
    <ScrollModal
      isOpen={open}
      onClose={handleCloseModal}
      title="Crear Flujo principal"
      description="Este flujo es iniciado por el tramitadór, al cual todos los flujos secundarios estarán conectados."
      srOnlyDescription={false}
      modalContentClassName="w-full max-w-[520px] max-h-[680px] flex flex-col pb-6"
    >
      <div className="flex py-8 justify-center flex-col items-center">
        <Heading variant="h2" className="mb-0!">
          Crear nuevo flujo
        </Heading>
        <p className="text-center text-body font-normal leading-3xl">
          Configura el nombre y origen de tu flujo antes de crearlo.
        </p>
      </div>
      <div className="flex flex-col">
        <form
          onSubmit={handleSubmit((data) => {
            onSubmit(data);
          })}
          className="flex flex-col flex-1 min-h-0 px-4 sm:px-8 gap-8"
        >
          <TextField
            label="Nombre del flujo"
            required
            error={errors.name?.message}
            placeholder="Escribe el nombre del flujo"
            {...register("name")}
          />
          <SelectField
            label="Padre del flujo"
            name="parent-flow"
            onChange={(parent_id) => {
              setValue("parent_flow_id", parent_id, { shouldValidate: true });
            }}
            options={selectOptions}
            placeholder="Selecciona un flujo..."
            value={parent_flow_id}
            isLoading={isLoadingFlows}
            isDisabled={isLoadingFlows && selectOptions.length === 0}
            required
            hasMore={hasNextPage}
            onLoadMore={fetchNextPage}
            error={errors.parent_flow_id?.message || ""}
          />
          <div className="w-full grid grid-cols-1 tablet:grid-cols-2 py-3 gap-3">
            <Button
              variant="tertiary"
              size="sm"
              type="button"
              disabled={isLoading}
              onClick={handleCloseModal}
              className="min-w-[120px] cursor-pointer"
            >
              Cancelar
            </Button>
            <Button
              variant="default"
              size="sm"
              type="submit"
              disabled={!isValid}
              isLoading={isLoading}
              className="min-w-[140px] cursor-pointer disabled:bg-neutral-object-accent disabled:text-on-disabled disabled:border-none disabled:shadow-none"
            >
              Aceptar
            </Button>
          </div>
        </form>
      </div>
    </ScrollModal>
  );
}
